import { useEffect, useState } from 'react';

import { RowData, SortingState, useReactTable } from '@tanstack/react-table';

import { useCreateInitialSearchObject } from '../Search/useInitialQuerySearch';

/**
 * This hook is used to set the initial sorting value from the url
 * if the hook ends up not being a valid column, it will reset the sorting
 *
 */
const useUrlSorting = <TData extends RowData>({
  getAllColumns,
  setSorting,
  resetSorting,
}: {
  getAllColumns: ReturnType<typeof useReactTable<TData>>['getAllColumns'];
  setSorting: (sorting: SortingState) => void;
  resetSorting: () => void;
}) => {
  const [didCheckSorting, setDidCheckSorting] = useState(false);
  const [didCheckIsColumnValid, setDidCheckIsColumnValid] = useState(false);
  const { getOrderValue } = useCreateInitialSearchObject();

  /** Set the initial sorting value from the url */
  useEffect(() => {
    if (didCheckSorting !== true) {
      const orderValue = getOrderValue();
      // Check if the table has a column with the id of the order value or if a column has the meta.sortKey of the order value
      if (orderValue) {
        setSorting([{ id: orderValue.field, desc: orderValue.direction === 'desc' }]);
      }
      setDidCheckSorting(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getOrderValue, setSorting]);

  /** Validate the sorting value from the url */
  useEffect(() => {
    const columns = getAllColumns();
    if (didCheckSorting && didCheckIsColumnValid !== true && columns.length !== 0) {
      const orderValue = getOrderValue();
      // Check if the table has a column with the id of the order value or if a column has the meta.sortKey of the order value
      if (orderValue) {
        const column = columns.find(
          (column) =>
            column.id === orderValue.field || column.columnDef?.meta?.sortKey === orderValue.field
        );

        if (!column) {
          resetSorting();
        }
      }
      setDidCheckIsColumnValid(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAllColumns, resetSorting, didCheckSorting]);
};

export { useUrlSorting };
